import React, { useEffect, useMemo } from 'react';
import { LayoutContext, type LayoutContextValue, type ShellSlotDefinition, type ShellSlotName } from '../../contexts/LayoutContext';
import { useViewportMetrics } from '../../hooks/useViewportMetrics';

const DESKTOP_MENU_RAIL_OFFSET = 'max(24px, calc((100vw - 1440px) / 2 + 24px))';

function createShellSlots(isMobileShell: boolean): Record<ShellSlotName, ShellSlotDefinition> {
  if (isMobileShell) {
    return {
      'top-utility-start': {
        className: 'fixed z-[9999] pointer-events-auto',
        style: {
          top: 'calc(var(--shell-safe-top, 0px) + 16px)',
          left: 'calc(var(--shell-safe-left, 0px) + 16px)',
        },
      },
      'top-utility-end': {
        className: 'fixed z-[9999] flex max-w-full items-center justify-end gap-2 pointer-events-auto',
        style: {
          top: 'calc(var(--shell-safe-top, 0px) + 16px)',
          right: 'calc(var(--shell-safe-right, 0px) + 16px)',
          left: 'calc(var(--shell-safe-left, 0px) + 72px)',
        },
      },
      'menu-content': {
        className: 'relative z-10 flex w-full flex-col items-center overflow-y-auto',
        style: {
          minHeight: 'var(--shell-viewport-height, 100vh)',
          paddingTop: 'calc(var(--shell-safe-top, 0px) + 72px)',
          paddingRight: 'calc(var(--shell-safe-right, 0px) + 12px)',
          paddingBottom: 'calc(var(--shell-safe-bottom, 0px) + 16px)',
          paddingLeft: 'calc(var(--shell-safe-left, 0px) + 12px)',
        },
      },
      'menu-secondary': {
        className: 'relative z-20 mt-4 flex w-full justify-center',
      },
      'menu-secondary-start': {
        className: 'hidden',
      },
      'menu-secondary-end': {
        className: 'hidden',
      },
      'floating-action': {
        className: 'fixed z-[9999] pointer-events-auto',
        style: {
          bottom: 'calc(var(--shell-safe-bottom, 0px) + 16px)',
          right: 'calc(var(--shell-safe-right, 0px) + 16px)',
        },
      },
    };
  }

  return {
    'top-utility-start': {
      className: 'fixed z-[9999] pointer-events-auto',
      style: {
        top: '31px',
        left: '31px',
      },
    },
    'top-utility-end': {
      className: 'fixed z-[9999] flex items-center gap-[9px] pointer-events-auto',
      style: {
        top: '31px',
        right: '31px',
      },
    },
    'menu-content': {
      className: 'relative z-10 flex min-h-screen w-full flex-col items-center justify-center px-6 py-16',
    },
    'menu-secondary': {
      className: 'hidden',
    },
    'menu-secondary-start': {
      className: 'fixed top-[130px] z-20 pointer-events-none',
      style: {
        left: DESKTOP_MENU_RAIL_OFFSET,
      },
    },
    'menu-secondary-end': {
      className: 'fixed top-[130px] z-20 pointer-events-none',
      style: {
        right: DESKTOP_MENU_RAIL_OFFSET,
      },
    },
    'floating-action': {
      className: 'fixed z-[9999] pointer-events-auto',
      style: {
        bottom: '16px',
        right: '31px',
      },
    },
  };
}

function toCssVariables(viewport: ReturnType<typeof useViewportMetrics>) {
  return {
    '--shell-viewport-width': `${viewport.width}px`,
    '--shell-viewport-height': `${viewport.height}px`,
    '--shell-layout-width': `${viewport.layoutWidth}px`,
    '--shell-layout-height': `${viewport.layoutHeight}px`,
    '--shell-offset-top': `${viewport.offsetTop}px`,
    '--shell-offset-left': `${viewport.offsetLeft}px`,
    '--shell-safe-top': `${viewport.safeArea.top}px`,
    '--shell-safe-right': `${viewport.safeArea.right}px`,
    '--shell-safe-bottom': `${viewport.safeArea.bottom}px`,
    '--shell-safe-left': `${viewport.safeArea.left}px`,
  };
}

const CSS_VARIABLE_NAMES = [
  '--shell-viewport-width',
  '--shell-viewport-height',
  '--shell-layout-width',
  '--shell-layout-height',
  '--shell-offset-top',
  '--shell-offset-left',
  '--shell-safe-top',
  '--shell-safe-right',
  '--shell-safe-bottom',
  '--shell-safe-left',
] as const;

interface MobileShellProviderProps {
  children: React.ReactNode;
}

const MobileShellProvider: React.FC<MobileShellProviderProps> = ({ children }) => {
  const viewport = useViewportMetrics();
  const isMobileShell = viewport.width <= 1024 || (viewport.isTouch && viewport.width <= 1280);
  const shellMode = isMobileShell ? 'mobile' : 'desktop';

  const slots = useMemo(() => createShellSlots(isMobileShell), [isMobileShell]);
  const cssVariables = useMemo(() => toCssVariables(viewport), [viewport]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const rootStyle = document.documentElement.style;
    for (const [name, value] of Object.entries(cssVariables)) {
      rootStyle.setProperty(name, value);
    }

    return () => {
      for (const variableName of CSS_VARIABLE_NAMES) {
        rootStyle.removeProperty(variableName);
      }
    };
  }, [cssVariables]);

  const value = useMemo<LayoutContextValue>(
    () => ({
      viewport,
      shellMode,
      isMobileShell,
      slots,
    }),
    [isMobileShell, shellMode, slots, viewport]
  );

  return (
    <LayoutContext.Provider value={value}>
      <div className="relative min-h-screen min-w-0" style={cssVariables as React.CSSProperties}>
        {children}
      </div>
    </LayoutContext.Provider>
  );
};

export default MobileShellProvider;
